import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppKey } from '../app/AppKey';
import { getApplicationLocale } from '../i18n/LocaleUtils';
import Logger from '../lib/Logger';
import { OAuthProviders } from '../oauth/OAuthProviders';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { updateKeyValue } from '../store/app/action-creators';
import { oAuthAuthStarted, oAuthAuthSucceeded, verifyOAuthVerificationPayload } from '../store/oauth/action-creators';
import { Tracker } from '../store/tracking/action-creators';
import GooglePlatform from './GooglePlatform';
const LOG = Logger.getLogger('useGoogleIdentityServices'); // extracted from https://developers.google.com/identity/gsi/web/reference/html-reference?hl=en#data-attributes

const GOOGLE_BUTTON_DATA_ATTRIBUTES = {
  text: 'continue_with',
  theme: 'filled_blue',
  context: 'signup',
  size: 'large'
};
var GoogleButtonWidth;

(function (GoogleButtonWidth) {
  GoogleButtonWidth[GoogleButtonWidth["Max"] = 400] = "Max";
  GoogleButtonWidth[GoogleButtonWidth["Mobile"] = 320] = "Mobile";
})(GoogleButtonWidth || (GoogleButtonWidth = {}));

const renderGoogleButton = (buttonElement, googleClientId, buttonWidth) => {
  if (!buttonElement || !googleClientId) {
    return;
  }

  GooglePlatform.getGoogleIdentityServicesClient().accounts.id.renderButton(buttonElement, Object.assign({}, GOOGLE_BUTTON_DATA_ATTRIBUTES, {
    locale: getApplicationLocale(),
    width: buttonWidth
  }));
};

export const useGoogleIdentityServices = ({
  googleButtonRef,
  libraryHasLoaded,
  googleSignInNonce,
  onAuthenticationStarted,
  onAuthenticationSucceeded,
  onVerificationStarted,
  onVerificationSucceeded,
  onVerificationFailed
}) => {
  const dispatch = useDispatch();
  const {
    shouldResizeGoogleButton
  } = useResponsiveness();
  const handleGoogleIdentityServicesCallback = useCallback(({
    credential
  }) => {
    dispatch(updateKeyValue(AppKey.WhichOAuthEndpointFailed, null));
    onAuthenticationStarted();
    dispatch(oAuthAuthStarted(OAuthProviders.Google));
    onVerificationStarted();
    onAuthenticationSucceeded({
      credential
    });
    dispatch(oAuthAuthSucceeded({
      token: credential
    }, OAuthProviders.Google));
    dispatch(Tracker.signupInteraction('google-button-click'));
    dispatch(updateKeyValue(AppKey.FirstOAuthPartnerClick, 'google'));
    dispatch(verifyOAuthVerificationPayload(credential, OAuthProviders.Google)).then(response => {
      onVerificationSucceeded(response);
    }).catch(error => {
      onVerificationFailed(error);
    });
  }, [dispatch, onAuthenticationSucceeded, onVerificationFailed, onVerificationSucceeded, onVerificationStarted, onAuthenticationStarted]);
  useEffect(() => {
    if (!googleSignInNonce || !libraryHasLoaded) {
      return;
    }

    const googleClientId = GooglePlatform.getClientId();
    const googleIdentityServicesClient = GooglePlatform.getGoogleIdentityServicesClient();

    if (!googleIdentityServicesClient || !googleIdentityServicesClient.accounts || !googleIdentityServicesClient.accounts.id) {
      LOG.warn('Google accounts or id are not existing properties.');
      return;
    }

    googleIdentityServicesClient.accounts.id.initialize({
      client_id: googleClientId,
      callback: handleGoogleIdentityServicesCallback,
      nonce: googleSignInNonce,
      auto_prompt: false
    });
    renderGoogleButton(googleButtonRef.current, googleClientId, shouldResizeGoogleButton ? GoogleButtonWidth.Mobile : GoogleButtonWidth.Max);
  }, [googleSignInNonce, libraryHasLoaded, googleButtonRef, handleGoogleIdentityServicesCallback, shouldResizeGoogleButton, onVerificationStarted, dispatch]);
};